import { Box, Divider, Grid, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IEnterprise from "../../../../interfaces/IEnterprise";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { GovernanceStatusData, RegistrationStatusEnum, RoomStatusData, RoomStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import EnterpriseService from '../../../../services/EnterpriseService';
import ImageField from "../../../../components/ImageField";
import DateField from "../../../../components/DateField";
import SelectField from "../../../../components/SelectField";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import { Fastfood, NetworkCheck } from "@mui/icons-material";
import { useAuthContext } from "../../../../contexts/AuthContext";
import Tooltip from "../../../../components/Tooltip";
import useState from "../../../../hooks/useState";


interface IStateValues {
    name: string;
    expiresAt: Date | undefined;
    partnerId: string;
    active: boolean;
    databaseTypeId: number | undefined;
    databaseHost: string;
    databasePort: string;
    databaseName: string;
    databaseUser: string;
    databasePassword: string;
    apiBaseUrl: string;
    apiAccessToken: string;
    ownerGuestTypeId: string;
    vipGuestTypeId: string;
    roomServicePointOfSaleId: string;
    requireMandatoryGovernanceItemsMarked: boolean;
    governanceStatusAllowedToDisplayGovernanceItems: string[];
    showOnlyParameterizedRoomTypes: boolean;
    showOnlyRoomsFromPool: boolean;
    analyticalDashboardUrl: string;
    managementDashboardUrl: string;
    financialDashboardUrl: string;
    vacationCenterDashboardUrl: string;
    serviceOrderMinimumNumberAttachments: number;
}


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IEnterprise>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { user } = useAuthContext();

    const state = useState<IStateValues>({
        name: '',
        expiresAt: undefined,
        partnerId: '',
        active: true,
        databaseTypeId: undefined,
        databaseHost: '',
        databasePort: '',
        databaseName: '',
        databaseUser: '',
        databasePassword: '',
        apiBaseUrl: '',
        apiAccessToken: '',
        ownerGuestTypeId: '',
        vipGuestTypeId: '',
        roomServicePointOfSaleId: '',
        requireMandatoryGovernanceItemsMarked: false,
        governanceStatusAllowedToDisplayGovernanceItems: [],
        showOnlyParameterizedRoomTypes: false,
        showOnlyRoomsFromPool: false,
        analyticalDashboardUrl: '',
        managementDashboardUrl: '',
        financialDashboardUrl: '',
        vacationCenterDashboardUrl: '',
        serviceOrderMinimumNumberAttachments: 0
    });


    useEffect(() => {
        if (open) {
            if (data) {
                state.update('name', data.name);
                state.update('expiresAt', data.expiresAt);
                state.update('partnerId', data.partnerId);
                state.update('active', Number(data.registrationStatusId) === RegistrationStatusEnum.Active);
                state.update('databaseTypeId', data.databaseTypeId);
                state.update('databaseHost', data.databaseHost);
                state.update('databasePort', data.databasePort);
                state.update('databaseName', data.databaseName);
                state.update('databaseUser', data.databaseUser);
                state.update('databasePassword', data.databasePassword);
                state.update('apiBaseUrl', data.apiBaseUrl);
                state.update('apiAccessToken', data.apiAccessToken);
                state.update('ownerGuestTypeId', data.ownerGuestTypeId);
                state.update('vipGuestTypeId', data.vipGuestTypeId);
                state.update('roomServicePointOfSaleId', data.roomServicePointOfSaleId);
                state.update('requireMandatoryGovernanceItemsMarked', data.requireMandatoryGovernanceItemsMarked);
                state.update('governanceStatusAllowedToDisplayGovernanceItems', data.governanceStatusAllowedToDisplayGovernanceItems);
                state.update('showOnlyParameterizedRoomTypes', data.showOnlyParameterizedRoomTypes);
                state.update('showOnlyRoomsFromPool', data.showOnlyRoomsFromPool);
                state.update('analyticalDashboardUrl', data.analyticalDashboardUrl);
                state.update('managementDashboardUrl', data.managementDashboardUrl);
                state.update('financialDashboardUrl', data.financialDashboardUrl);
                state.update('vacationCenterDashboardUrl', data.vacationCenterDashboardUrl);
                state.update('serviceOrderMinimumNumberAttachments', data.serviceOrderMinimumNumberAttachments);
            } else {
                state.reset();
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id && data.id !== 0) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        EnterpriseService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const handleTestDatabaseConnection = useCallback(() => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'databaseTypeId', state.values.databaseTypeId);
        formData = FormHelper.Add(formData, 'databaseHost', state.values.databaseHost);
        formData = FormHelper.Add(formData, 'databasePort', state.values.databasePort);
        formData = FormHelper.Add(formData, 'databaseName', state.values.databaseName);
        formData = FormHelper.Add(formData, 'databaseUser', state.values.databaseUser);
        formData = FormHelper.Add(formData, 'databasePassword', state.values.databasePassword);

        EnterpriseService.TestDatabaseConnection(formData);
        // eslint-disable-next-line
    }, [state.values]);


    const handleTestApiConnection = useCallback(() => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'apiBaseUrl', state.values.apiBaseUrl);
        formData = FormHelper.Add(formData, 'apiAccessToken', state.values.apiAccessToken);

        EnterpriseService.TestApiConnection(formData);
        // eslint-disable-next-line
    }, [state.values]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data && data.id !== 0 ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('name')} 
                        name='name' 
                        value={state.values.name} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <DateField
                        label={translate('expiresAt')}
                        format='DD/MM/YYYY'
                        name='expiresAt'
                        value={state.values.expiresAt}
                        onChange={value => state.update('expiresAt', value)}
                        disabled={data && !user?.administrator}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('partnerId')} 
                        name='partnerId' 
                        value={state.values.partnerId} 
                        required={!data?.administrator}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 20))}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('active')}
                        name='registrationStatusId'
                        value={state.values.active ? RegistrationStatusEnum.Active : RegistrationStatusEnum.Inactive}
                        checked={state.values.active}
                        onChange={e => state.update('active', e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={translate('images')} />
                </Grid>

                <Grid item md={4} sm={6} xs={12} marginTop={2}>
                    <ImageField
                        title={translate('logo')}
                        name='logo'
                        defaultImage={data?.logo}
                        height={200}
                        required={false}
                    />
                </Grid>

                <Grid item md={4} sm={6} xs={12} marginTop={2}>
                    <ImageField
                        title={translate('illustration')}
                        name='illustration'
                        defaultImage={data?.illustration}
                        height={200}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item md={6} xs={8}>
                    <DialogSectionTitle title={translate('database')} />
                </Grid>

                <Grid item md={6} xs={4} className='textalign-right'>
                    <Tooltip title={translate('testDatabaseConnection')}>
                        <IconButton onClick={handleTestDatabaseConnection}>
                            <NetworkCheck />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item md={6} xs={12} marginTop={1}>
                    <SelectField 
                        label={translate('type')} 
                        name='databaseTypeId' 
                        value={state.values.databaseTypeId}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        required={!data?.administrator}
                        data={environment.databaseTypeList.map(d => ({ value: d.id, text: d.description }))}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('host')} 
                        name='databaseHost' 
                        value={state.values.databaseHost} 
                        required={!data?.administrator}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 200))}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('port')} 
                        name='databasePort' 
                        value={state.values.databasePort} 
                        required={!data?.administrator}
                        type='number'
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 10))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('databaseName')} 
                        name='databaseName' 
                        value={state.values.databaseName} 
                        required={!data?.administrator}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 50))}
                    />
                </Grid>

                <Grid item md={3} xs={12}>
                    <TextField 
                        label={translate('user')} 
                        name='databaseUser' 
                        value={state.values.databaseUser} 
                        required={!data?.administrator}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 50))}
                    />
                </Grid>

                <Grid item md={3} xs={12}>
                    <TextField 
                        label={translate('password')} 
                        name='databasePassword' 
                        value={state.values.databasePassword} 
                        required={!data?.administrator}
                        type='password'
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 50))}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item md={6} xs={8}>
                    <DialogSectionTitle title={translate('api')} />
                </Grid>

                <Grid item md={6} xs={4} className='textalign-right'>
                    <Tooltip title={translate('testApiConnection')}>
                        <IconButton onClick={handleTestApiConnection}>
                            <NetworkCheck />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('baseUrl')} 
                        name='apiBaseUrl' 
                        value={state.values.apiBaseUrl} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 200))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('accessToken')} 
                        name='apiAccessToken' 
                        value={state.values.apiAccessToken} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 50))}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.governanceModuleName} />
                </Grid>

                <Grid item md={8} xs={12}>
                    <SelectField
                        label={translate('governanceStatusAllowedToDisplayGovernanceItems')}
                        name='governanceStatusAllowedToDisplayGovernanceItems'
                        value={state.values.governanceStatusAllowedToDisplayGovernanceItems}
                        required={false}
                        multiple={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: s.partnerId,
                                text: translate(s.name)
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('requireMandatoryGovernanceItemsMarked')}
                        name='requireMandatoryGovernanceItemsMarked'
                        value={state.values.requireMandatoryGovernanceItemsMarked}
                        checked={state.values.requireMandatoryGovernanceItemsMarked}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginTop={2} />

                <Grid item md={6} xs={12} marginTop={2}>
                    <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                        {translate('guestTypes')}
                    </Typography>

                    <Box marginBottom={1} />

                    <Table className='borderradius-default backgroundcolor-grey-1'>
                        <TableBody>
                            {
                                [
                                    {
                                        label: translate('owner'),
                                        name: 'ownerGuestTypeId',
                                        value: state.values.ownerGuestTypeId,
                                        enumProps: RoomStatusData[RoomStatusEnum.OwnerReservation],
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 20))
                                    }, {
                                        label: translate('vip'),
                                        name: 'vipGuestTypeId',
                                        value: state.values.vipGuestTypeId,
                                        enumProps: RoomStatusData[RoomStatusEnum.VipReservation],
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 20))
                                    }
                                ].map((row, index) => {
                                    return <TableRow key={index}>
                                        <TableCell>
                                            <Box className='display-flex alignitems-center'>
                                                <row.enumProps.Icon sx={{ color: `${row.enumProps.color} !important` }} />
                                                <Typography variant="subtitle2" className='textwrap-nowrap' marginLeft={1.2}>{row.label}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell className='padding-none'>
                                            <TextField 
                                                name={row.name} 
                                                value={row.value}
                                                required={false}
                                                onChange={e => row.onChange(e)}
                                                fullWidth={false}
                                                margin="none"
                                                placeholder="..."
                                                InputProps={{
                                                    disableUnderline: true,
                                                    className: 'backgroundcolor-transparent borderradius-none'
                                                }}
                                                inputProps={{
                                                    className: 'textalign-right',
                                                    style: {
                                                        paddingTop: 16,
                                                        paddingRight: 12,
                                                        paddingBottom: 16,
                                                        paddingLeft: 12,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item md={6} xs={12} marginTop={2}>
                    <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                        {translate('pointsOfSale')}
                    </Typography>

                    <Box marginBottom={1} />

                    <Table className='borderradius-default backgroundcolor-grey-1'>
                        <TableBody>
                            {
                                [
                                    {
                                        label: translate('roomService'),
                                        name: 'roomServicePointOfSaleId',
                                        value: state.values.roomServicePointOfSaleId,
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 20))
                                    }
                                ].map((row, index) => {
                                    return <TableRow key={index}>
                                        <TableCell>
                                            <Box className='display-flex alignitems-center'>
                                                <Fastfood />
                                                <Typography variant="subtitle2" className='textwrap-nowrap' marginLeft={1.2}>{row.label}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell className='padding-none'>
                                            <TextField 
                                                name={row.name} 
                                                value={row.value}
                                                required={false}
                                                onChange={e => row.onChange(e)}
                                                fullWidth={false}
                                                margin="none"
                                                placeholder="..."
                                                InputProps={{
                                                    disableUnderline: true,
                                                    className: 'backgroundcolor-transparent borderradius-none'
                                                }}
                                                inputProps={{
                                                    className: 'textalign-right',
                                                    style: {
                                                        paddingTop: 16,
                                                        paddingRight: 12,
                                                        paddingBottom: 16,
                                                        paddingLeft: 12,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item xs={12} marginTop={4} />

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('showOnlyRoomsFromPool')}
                        name='showOnlyRoomsFromPool'
                        value={state.values.showOnlyRoomsFromPool}
                        checked={state.values.showOnlyRoomsFromPool}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('showOnlyParameterizedRoomTypes')}
                        name='showOnlyParameterizedRoomTypes'
                        value={state.values.showOnlyParameterizedRoomTypes}
                        checked={state.values.showOnlyParameterizedRoomTypes}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.serviceModuleName} />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('minimumNumberAttachments')} 
                        name='serviceOrderMinimumNumberAttachments' 
                        value={state.values.serviceOrderMinimumNumberAttachments} 
                        type='number'
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 2))}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.analyticsModuleName} />
                </Grid>

                <Grid item md={12} xs={12}>
                    <TextField 
                        type='url'
                        label={translate('analyticalDashboardUrl')} 
                        name='analyticalDashboardUrl' 
                        value={state.values.analyticalDashboardUrl} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 500))}
                    />
                </Grid>

                <Grid item md={12} xs={12}>
                    <TextField 
                        type='url'
                        label={translate('managementDashboardUrl')} 
                        name='managementDashboardUrl' 
                        value={state.values.managementDashboardUrl} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 500))}
                    />
                </Grid>

                <Grid item md={12} xs={12}>
                    <TextField 
                        type='url'
                        label={translate('financialDashboardUrl')} 
                        name='financialDashboardUrl' 
                        value={state.values.financialDashboardUrl} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 500))}
                    />
                </Grid>

                <Grid item md={12} xs={12}>
                    <TextField 
                        type='url'
                        label={translate('vacationCenterDashboardUrl')} 
                        name='vacationCenterDashboardUrl' 
                        value={state.values.vacationCenterDashboardUrl} 
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 500))}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;